import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';
import TrustStamp from '../components/TrustStamp';
import CtaSection from '../components/CtaSection';
import { Brain, Pill } from 'lucide-react';

export default function Ocd() {
  const { lang, getLocalizedValue } = useLanguage();
  const [cmsData, setCmsData] = useState(null);

  useEffect(() => {
    const fetchCmsData = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/ocd_side');
        if (res.ok) {
          const items = await res.json();
          if (Array.isArray(items) && items.length > 0) {
            setCmsData(items[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching ocd_side CMS data:', err);
      }
    };
    fetchCmsData();
  }, []);

  const seoTitle = lang === 'da' 
    ? 'OCD (Obsessiv-kompulsiv tilstand) – Behandling | Nordisk Psykiatri' 
    : 'OCD (Tvångssyndrom) – Behandling & Terapi | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'OCD er præget af tilbagevendende tvangstanker og tvangshandlinger. Læs om symptomer, typologier og effektiv evidensbaseret behandling hos Nordisk Psykiatri.'
    : 'OCD (tvångssyndrom) kännetecknas av återkommande tvångstankar och tvångshandlingar. Läs om symtom och effektiv evidensbaserad behandling hos Nordisk Psykiatri.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  const defaultHeroImage = "https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/eyh_3294d5f0.avif";
  const heroImgUrl = cmsData?.hero_billede?.[0] || defaultHeroImage;

  return (
    <div className="bg-sand-light min-h-screen text-text-dark font-sans selection:bg-petrol-dark/10 selection:text-petrol-dark">
      
      {/* ==========================================
          SEKTION 1 – HERO (FADE-EFFEKT SOM PÅ FORSIDEN)
          ========================================== */}
      <section className="relative w-full bg-white overflow-hidden pt-4 sm:pt-6 lg:pt-8 pb-0 border-none outline-none">
        
        {/* Desktop Full-Bleed Image Container on Right with horizontal fade */}
        <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-[60%] xl:w-[65%] 2xl:w-[68%] h-full z-10 overflow-hidden pointer-events-none select-none">
          <img
            src={heroImgUrl}
            alt={cmsData?.hero_overskrift ? getLocalizedValue(cmsData.hero_overskrift) : "Rolige og eksklusive rammer hos Nordisk Psykiatri"}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-center"
            style={{
              maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.08) 12%, rgba(0,0,0,0.3) 28%, rgba(0,0,0,0.65) 48%, rgba(0,0,0,0.92) 72%, black 90%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.08) 12%, rgba(0,0,0,0.3) 28%, rgba(0,0,0,0.65) 48%, rgba(0,0,0,0.92) 72%, black 90%)'
            }}
          />

          {/* Smooth Solid White Overlay Fade to guarantee seamless blending on the left */}
          <div className="absolute inset-y-0 left-0 w-[45%] bg-gradient-to-r from-white via-white/60 to-transparent pointer-events-none" />

          {/* Soft Bottom Transition to Sand-light section */}
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-sand-light via-sand-light/60 to-transparent pointer-events-none" />
        </div>

        {/* Text Container aligned with site grid */}
        <div className="w-full max-w-[1280px] mx-auto px-6 lg:px-8 relative min-h-[480px] lg:min-h-[540px] xl:min-h-[580px] flex flex-col justify-center z-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 items-center w-full relative z-20">
            
            {/* Left Text Area */}
            <div className="lg:col-span-6 xl:col-span-5 py-10 sm:py-14 lg:py-16 pr-0 lg:pr-6 flex flex-col justify-center z-30">
              
              <span className="inline-block text-xs font-bold uppercase tracking-[0.16em] text-petrol-light mb-3">
                {cmsData?.hero_badge ? getLocalizedValue(cmsData.hero_badge) : 'OCD'}
              </span>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-normal text-petrol-dark leading-[1.12] tracking-tight mb-2">
                {cmsData?.hero_overskrift ? getLocalizedValue(cmsData.hero_overskrift) : 'OCD'}
              </h1>

              <p className="font-display italic text-xl sm:text-2xl text-petrol-dark/90 mb-4">
                {cmsData?.hero_undertitel ? getLocalizedValue(cmsData.hero_undertitel) : '(Obsessiv-kompulsiv lidelse)'}
              </p>

              {/* Accent Divider */}
              <div className="h-[2px] w-10 bg-[#D1E1BE] rounded-full mb-6" />

              <div className="font-sans text-base sm:text-lg text-text-dark/85 leading-relaxed font-normal mb-8 [&>p]:mb-3 [&>p:last-child]:mb-0">
                {cmsData?.hero_beskrivelse ? (
                  <div dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsData.hero_beskrivelse) }} />
                ) : (
                  <p>OCD er en angstlidelse med tilbagevendende tvangstanker og/eller tvangshandlinger, som kan være meget belastende og påvirke hverdagen betydeligt.</p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-1">
                {(() => {
                  const bookingHref = cmsData?.hero_cta_link || cmsData?.cta_knap_link || "/kontakt";
                  const isExt = typeof bookingHref === 'string' && (bookingHref.startsWith('http://') || bookingHref.startsWith('https://'));
                  return isExt ? (
                    <a
                      href={bookingHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-full bg-petrol-dark text-white px-7 py-3.5 text-sm font-semibold hover:bg-petrol-medium transition-all duration-200 shadow-sm cursor-pointer"
                    >
                      {cmsData?.hero_cta_tekst ? getLocalizedValue(cmsData.hero_cta_tekst) : (lang === 'da' ? 'Book en indledende samtale' : 'Boka inledande samtal')}
                    </a>
                  ) : (
                    <Link
                      to={bookingHref}
                      className="inline-flex items-center justify-center rounded-full bg-petrol-dark text-white px-7 py-3.5 text-sm font-semibold hover:bg-petrol-medium transition-all duration-200 shadow-sm cursor-pointer"
                    >
                      {cmsData?.hero_cta_tekst ? getLocalizedValue(cmsData.hero_cta_tekst) : (lang === 'da' ? 'Book en indledende samtale' : 'Boka inledande samtal')}
                    </Link>
                  );
                })()}

                <Link
                  to="/priser"
                  className="inline-flex items-center justify-center rounded-full bg-transparent hover:bg-petrol-dark/5 text-petrol-dark border border-petrol-dark/60 hover:border-petrol-dark px-7 py-3.5 text-sm font-semibold transition-all duration-200 shadow-xs"
                >
                  {lang === 'da' ? 'Se priser' : 'Se priser'}
                </Link>
              </div>

              {/* Fagligt gennemgået / Review trust stamp */}
              <TrustStamp
                text={cmsData?.fagligt_gennemgaaet ? getLocalizedValue(cmsData.fagligt_gennemgaaet) : (lang === 'da' ? 'Fagligt gennemgået af Felix Klich, klinisk psykolog | Opdateret september 2026' : 'Clinically reviewed by Felix Klich, Clinical Psychologist | Updated September 2026')}
              />
            </div>

            {/* Mobile/Tablet Spacer on Grid */}
            <div className="lg:col-span-6 xl:col-span-7 block lg:hidden" />

          </div>
        </div>

        {/* MOBILE & TABLET IMAGE (< lg): Soft Top-Edge Alpha Masking & Smooth Bottom Fade */}
        <div className="block lg:hidden relative h-[320px] sm:h-[380px] w-full overflow-hidden mt-2 bg-white">
          <img
            src={heroImgUrl}
            alt={cmsData?.hero_overskrift ? getLocalizedValue(cmsData.hero_overskrift) : "Rolige og eksklusive rammer hos Nordisk Psykiatri"}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-[38%_center]"
            style={{
              maskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.1) 8%, rgba(0,0,0,0.4) 25%, rgba(0,0,0,0.85) 60%, black 85%)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.1) 8%, rgba(0,0,0,0.4) 25%, rgba(0,0,0,0.85) 60%, black 85%)'
            }}
          />
          <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white to-transparent pointer-events-none" />
        </div>

        {/* Full-Width Soft Bottom Gradient Transition Fading into Next Section */}
        <div className="absolute bottom-0 inset-x-0 h-16 sm:h-20 lg:h-24 bg-gradient-to-t from-sand-light via-sand-light/60 to-transparent pointer-events-none z-25" />

      </section>


      {/* ==========================================
          SEKTION 2 – HVAD ER OCD? OG "VIDSTE DU?"
          ========================================== */}
      <section className="py-16 lg:py-24 bg-sand-light border-b border-sand-medium/40">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Left Column: Primary Content */}
            <div className="lg:col-span-5 space-y-6">
              <div className="space-y-4">
                <h2 className="font-display text-3xl sm:text-4xl text-petrol-dark font-normal tracking-tight">
                  {cmsData?.hvad_er_overskrift ? getLocalizedValue(cmsData.hvad_er_overskrift) : 'Hvad er OCD?'}
                </h2>
                <div className="w-10 h-[1.5px] bg-petrol-dark"></div>
              </div>

              <div className="space-y-4 text-[15px] sm:text-base leading-relaxed text-text-dark/85 [&>p]:mb-4 [&>p:last-child]:mb-0">
                {cmsData?.hvad_er_tekst ? (
                  <div dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsData.hvad_er_tekst) }} />
                ) : (
                  <>
                    <p>
                      OCD er kendetegnet ved uønskede og påtrængende tanker (tvangstanker) og gentagne handlinger eller ritualer (tvangshandlinger), som man føler sig nødt til at udføre for at mindske angst eller forebygge noget frygtet.
                    </p>
                    <p>
                      Lidelsen kan påvirke mange områder af livet og medføre stor mistrivsel, skam og isolation – men OCD kan behandles effektivt.
                    </p>
                  </>
                )}
              </div>

              <div className="pt-2">
                <a
                  href="#symptomer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-petrol-dark group"
                >
                  <span>{lang === 'da' ? 'Læs mere om OCD' : 'Läs mer om OCD'}</span>
                  <span className="transition-transform duration-150 group-hover:translate-x-1" aria-hidden="true">&rarr;</span>
                </a>
              </div>
            </div>

            {/* Right Column: Stats & Meta (Light Minimal Editorial Design) */}
            <div className="lg:col-span-7 space-y-6 lg:pt-2">
              <div>
                <span className="inline-block px-2.5 py-1 text-[11px] font-semibold tracking-widest uppercase bg-[#D1E1BE] text-petrol-dark rounded-none">
                  {cmsData?.vidste_du_badge ? getLocalizedValue(cmsData.vidste_du_badge) : 'VIDSTE DU?'}
                </span>
              </div>

              {/* Stat Cards / Comparison Matrix */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 border-b border-[#dee4e0] pb-8">
                <div className="sm:border-r sm:border-[#dee4e0] sm:pr-8 space-y-2">
                  <div className="font-display text-4xl sm:text-5xl text-petrol-dark font-normal">
                    25–30 %
                  </div>
                  <p className="text-sm leading-relaxed text-text-dark/85">
                    {lang === 'da' ? 'af personer med ADHD har også OCD.' : 'of people with ADHD also have OCD.'}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="font-display text-4xl sm:text-5xl text-petrol-dark font-normal">
                    20–25 %
                  </div>
                  <p className="text-sm leading-relaxed text-text-dark/85">
                    {lang === 'da' ? 'af personer med autisme har også OCD.' : 'of people with autism also have OCD.'}
                  </p>
                </div>
              </div>

              {/* Stat Footnote */}
              <div className="text-xs sm:text-[13px] leading-relaxed text-text-dark/80 max-w-xl [&>p]:mb-2 [&>p:last-child]:mb-0">
                {cmsData?.vidste_du_tekst ? (
                  <div dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsData.vidste_du_tekst) }} />
                ) : (
                  <p>
                    {lang === 'da' 
                      ? 'Komorbiditet er almindeligt, og det betyder, at man kan have flere diagnoser samtidigt. Det er vigtigt at forstå hele billedet for at kunne tilbyde den rette behandling.'
                      : 'Comorbidity is common, which means one can have multiple diagnoses simultaneously. Understanding the full picture is essential to offering tailored, effective care.'}
                  </p>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ==========================================
          SEKTION 3 – TYPISKE SYMPTOMER
          ========================================== */}
      <section id="symptomer" className="py-20 sm:py-24 lg:py-28 bg-[#f5efe5] border-b border-sand-medium/40 scroll-mt-12">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          
          {/* Section Header with Minimalist Accent Pip */}
          <div className="flex flex-col items-center gap-3 mb-16 md:mb-20 text-center">
            <h2 className="font-display text-2xl sm:text-3xl md:text-[32px] text-[#232622] font-normal tracking-tight">
              {cmsData?.symptomer_overskrift ? getLocalizedValue(cmsData.symptomer_overskrift) : (lang === 'da' ? 'Typiske symptomer' : 'Typical Symptoms of OCD')}
            </h2>
            <div className="w-8 h-[2px] bg-[#9EA895] rounded-full" />
          </div>

          {/* Symmetrical 3-Column Radial Grid / Mobile Stack */}
          <div className="relative max-w-[940px] mx-auto">
            
            {/* Desktop / Tablet Connector Lines (SVG Layer) */}
            <svg 
              className="hidden md:block absolute inset-0 w-full h-full pointer-events-none -z-0 overflow-visible"
              viewBox="0 0 940 380"
              preserveAspectRatio="xMidYMid meet"
              aria-hidden="true"
            >
              {/* Top-Left: Tvangstanker -> 45° dogleg into circle */}
              <path 
                d="M 265,55 L 325,55 L 365,95" 
                stroke="#9EA895" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                fill="none" 
              />
              <circle cx="265" cy="55" r="2.5" fill="#9EA895" />

              {/* Bottom-Left: Undgåelse -> 45° dogleg into circle */}
              <path 
                d="M 265,325 L 325,325 L 365,285" 
                stroke="#9EA895" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                fill="none" 
              />
              <circle cx="265" cy="325" r="2.5" fill="#9EA895" />

              {/* Top-Right: Tvangshandlinger -> 45° dogleg into circle */}
              <path 
                d="M 675,55 L 615,55 L 575,95" 
                stroke="#9EA895" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                fill="none" 
              />
              <circle cx="675" cy="55" r="2.5" fill="#9EA895" />

              {/* Bottom-Right: Usikkerhed og tvivl -> 45° dogleg into circle */}
              <path 
                d="M 675,325 L 615,325 L 575,285" 
                stroke="#9EA895" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                fill="none" 
              />
              <circle cx="675" cy="325" r="2.5" fill="#9EA895" />
            </svg>

            {/* Grid Container */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_300px_1fr] lg:grid-cols-[1fr_320px_1fr] items-center gap-10 md:gap-0 min-h-auto md:min-h-[380px] relative z-10">
              
              {/* Left Satellite Column */}
              <div className="flex flex-col justify-between h-full space-y-10 md:space-y-0 md:h-[360px] md:pr-14 lg:pr-16 text-center md:text-right items-center md:items-end">
                
                {/* Node Top Left: Tvangstanker */}
                <div className="space-y-1.5 max-w-[240px] md:max-w-[195px] lg:max-w-[205px]">
                  <h3 className="font-display text-lg md:text-xl text-[#232622] font-normal">
                    {lang === 'da' ? 'Tvangstanker' : 'Obsessions'}
                  </h3>
                  <p className="font-sans text-xs md:text-[13px] leading-relaxed text-[#575B54] font-light">
                    {lang === 'da'
                      ? 'Påtrængende og uønskede tanker, billeder eller impulser, som skaber angst eller ubehag.'
                      : 'Intrusive, unwanted thoughts, images, or impulses that cause distress or anxiety.'}
                  </p>
                </div>

                {/* Node Bottom Left: Undgåelse */}
                <div className="space-y-1.5 max-w-[240px] md:max-w-[195px] lg:max-w-[205px]">
                  <h3 className="font-display text-lg md:text-xl text-[#232622] font-normal">
                    {lang === 'da' ? 'Undgåelse' : 'Avoidance'}
                  </h3>
                  <p className="font-sans text-xs md:text-[13px] leading-relaxed text-[#575B54] font-light">
                    {lang === 'da'
                      ? 'Situationer eller aktiviteter undgås, fordi de kan udløse tvangstanker, angst eller ritualer.'
                      : 'Situations or activities are avoided because they trigger obsessions, anxiety, or rituals.'}
                  </p>
                </div>

              </div>

              {/* Center Circle Hub */}
              <div className="flex items-center justify-center">
                <div className="w-64 h-64 sm:w-72 sm:h-72 md:w-[290px] md:h-[290px] lg:w-[310px] lg:h-[310px] rounded-full border border-[#9EA895] bg-[#f5efe5] flex flex-col items-center justify-center p-6 sm:p-8 text-center shadow-xs">
                  <h4 className="font-display text-xl md:text-2xl text-[#232622] leading-snug mb-2.5 font-normal">
                    {cmsData?.symptomer_center_titel ? getLocalizedValue(cmsData.symptomer_center_titel) : (
                      lang === 'da' ? (
                        <>Symptomerne kan<br />forstærke hinanden</>
                      ) : (
                        <>Symptoms Can<br />Reinforce Each Other</>
                      )
                    )}
                  </h4>
                  <p className="font-sans text-xs md:text-[13px] text-[#575B54] font-light leading-relaxed max-w-[200px]">
                    {lang === 'da' ? 'Tanker, handlinger, undgåelse og tvivl hænger ofte tæt sammen.' : 'Thoughts, compulsions, avoidance, and doubt are closely interconnected.'}
                  </p>
                </div>
              </div>

              {/* Right Satellite Column */}
              <div className="flex flex-col justify-between h-full space-y-10 md:space-y-0 md:h-[360px] md:pl-14 lg:pl-16 text-center md:text-left items-center md:items-start">
                
                {/* Node Top Right: Tvangshandlinger */}
                <div className="space-y-1.5 max-w-[240px] md:max-w-[195px] lg:max-w-[205px]">
                  <h3 className="font-display text-lg md:text-xl text-[#232622] font-normal">
                    {lang === 'da' ? 'Tvangshandlinger' : 'Compulsions'}
                  </h3>
                  <p className="font-sans text-xs md:text-[13px] leading-relaxed text-[#575B54] font-light">
                    {lang === 'da'
                      ? 'Handlinger eller mentale ritualer, der udføres for at dæmpe ubehaget eller forhindre noget frygtet.'
                      : 'Repetitive behaviors or mental rituals performed to reduce anxiety or prevent a feared event.'}
                  </p>
                </div>

                {/* Node Bottom Right: Usikkerhed og tvivl */}
                <div className="space-y-1.5 max-w-[240px] md:max-w-[195px] lg:max-w-[205px]">
                  <h3 className="font-display text-lg md:text-xl text-[#232622] font-normal">
                    {lang === 'da' ? 'Usikkerhed og tvivl' : 'Uncertainty & Doubt'}
                  </h3>
                  <p className="font-sans text-xs md:text-[13px] leading-relaxed text-[#575B54] font-light">
                    {lang === 'da'
                      ? 'Behovet for sikkerhed kan føre til kontrol, gentagne spørgsmål og vanskeligheder ved at vælge.'
                      : 'The intense urge for certainty leads to checking, repetitive reassurance-seeking, and hesitation.'}
                  </p>
                </div>

              </div>

            </div>

          </div>

        </div>
      </section>


      {/* ==========================================
          SEKTION 4 – OCD KAN BEHANDLES EFFEKTIVT
          ========================================== */}
      <section className="py-16 lg:py-24 bg-sand-light border-b border-sand-medium/40 overflow-hidden">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Billede venstre kolonne (fade blødt ud mod højre) */}
            <div className="lg:col-span-6 relative rounded-3xl overflow-hidden shadow-sm aspect-[16/10] sm:aspect-[16/9] lg:aspect-[4/3]">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/ocd-brain-network-abstract.jpg"
                alt="Neurale mønstre og organisk celle-struktur"
                className="w-full h-full object-cover"
              />
              {/* Blød organisk fade-overgang i højre side */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-sand-light/30 to-sand-light hidden lg:block" />
            </div>

            {/* Tekst højre kolonne */}
            <div className="lg:col-span-6 lg:pl-6">
              <div className="border-l-2 border-petrol-dark/40 pl-6 sm:pl-8 py-2">
                <p className="font-display text-2xl sm:text-3xl lg:text-4xl text-petrol-dark leading-tight font-normal">
                  {cmsData?.citat_banner_tekst ? getLocalizedValue(cmsData.citat_banner_tekst) : (
                    <>
                      OCD kan behandles effektivt.<br />
                      De fleste oplever betydelig<br />
                      symptomreduktion med den<br />
                      rette behandling og støtte.
                    </>
                  )}
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ==========================================
          SEKTION 5 – BEHANDLING
          ========================================== */}
      <section id="behandling" className="py-16 sm:py-20 lg:py-24 bg-[#fbfaf7] border-b border-sand-medium/40 scroll-mt-12 text-[#163f3a]">
        <div className="w-full max-w-[1180px] mx-auto px-6 lg:px-8">
          
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal leading-[1.1] text-[#163f3a]">
              {cmsData?.behandling_overskrift ? getLocalizedValue(cmsData.behandling_overskrift) : (lang === 'da' ? 'Behandling' : 'OCD Treatment')}
            </h2>
            <div className="w-14 h-[2px] bg-[#163f3a] mx-auto mt-4 sm:mt-5" />
          </div>

          {/* To kolonner med midterstreg og forbedrede ikoner */}
          <div className="grid grid-cols-1 md:grid-cols-2">
            
            {/* PSYKOLOGISK BEHANDLING */}
            <div className="md:pr-8 lg:pr-10 pb-12 md:pb-0 flex flex-col">
              <div className="mb-5 text-[#163f3a] flex items-center">
                <Brain className="w-10 h-10 stroke-[1.5]" />
              </div>

              <h3 className="font-display text-2xl sm:text-3xl lg:text-[34px] font-normal leading-[1.15] text-[#163f3a] mb-3.5">
                {lang === 'da' ? 'Psykologisk behandling' : 'Psychological Treatment'}
              </h3>

              <p className="font-sans text-base sm:text-lg leading-relaxed text-[#35443f] max-w-none mb-0">
                {lang === 'da'
                  ? 'Førstevalgsbehandling ved OCD er psykologisk behandling, særligt eksponering med responshindring (ERP).'
                  : 'The first-line treatment for OCD is psychological therapy, particularly Exposure and Response Prevention (ERP).'}
              </p>

              <div className="w-12 h-[1px] bg-[#aab8b2] my-6 sm:my-7" />

              <ul className="space-y-3.5 font-sans text-sm sm:text-base leading-relaxed text-[#35443f]">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#163f3a] mt-2 shrink-0" />
                  <span>{lang === 'da' ? 'Eksponering med responshindring (ERP)' : 'Exposure and Response Prevention (ERP)'}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#163f3a] mt-2 shrink-0" />
                  <span>{lang === 'da' ? 'Kognitiv adfærdsterapi' : 'Cognitive Behavioral Therapy (CBT)'}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#163f3a] mt-2 shrink-0" />
                  <span>{lang === 'da' ? 'ACT og metakognitiv terapi' : 'ACT & Metacognitive Therapy'}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#163f3a] mt-2 shrink-0" />
                  <span>{lang === 'da' ? 'Psykoedukation og strategier til hverdagen' : 'Psychoeducation & everyday coping strategies'}</span>
                </li>
              </ul>
            </div>

            {/* MEDICINSK BEHANDLING */}
            <div className="md:pl-8 lg:pl-10 pt-12 md:pt-0 border-t md:border-t-0 md:border-l border-[#cad5d0] flex flex-col">
              <div className="mb-5 text-[#163f3a] flex items-center">
                <Pill className="w-10 h-10 stroke-[1.5]" />
              </div>

              <h3 className="font-display text-2xl sm:text-3xl lg:text-[34px] font-normal leading-[1.15] text-[#163f3a] mb-3.5">
                {lang === 'da' ? 'Medicinsk behandling' : 'Medical Treatment'}
              </h3>

              <p className="font-sans text-base sm:text-lg leading-relaxed text-[#35443f] max-w-[500px] mb-0">
                {lang === 'da'
                  ? 'Ved moderat til svær OCD kan medicin være en vigtig del af behandlingen – ofte i kombination med terapi.'
                  : 'In moderate to severe OCD, medication can be an important part of treatment—frequently in combination with therapy.'}
              </p>

              <div className="w-12 h-[1px] bg-[#aab8b2] my-6 sm:my-7" />

              <ul className="space-y-3.5 font-sans text-sm sm:text-base leading-relaxed text-[#35443f]">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#163f3a] mt-2 shrink-0" />
                  <span>{lang === 'da' ? 'SSRI-præparater' : 'SSRI medications'}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#163f3a] mt-2 shrink-0" />
                  <span>{lang === 'da' ? 'Psykiatrisk vurdering' : 'Psychiatric evaluation'}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#163f3a] mt-2 shrink-0" />
                  <span>{lang === 'da' ? 'Løbende opfølgning' : 'Continuous follow-up'}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#163f3a] mt-2 shrink-0" />
                  <span>{lang === 'da' ? 'Individuelt tilpasset behandling' : 'Individually tailored treatment'}</span>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>


      {/* ==========================================
          SEKTION 6 – OCD KAN SE UD PÅ FORSKELLIGE MÅDER
          ========================================== */}
      <section className="py-20 lg:py-28 bg-white border-b border-sand-medium/40">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* Venstre kolonne */}
            <div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-petrol-dark tracking-tight leading-tight mb-3">
                {cmsData?.former_intro_overskrift ? getLocalizedValue(cmsData.former_intro_overskrift) : (
                  <>OCD kan se ud på<br />forskellige måder</>
                )}
              </h2>
              {/* Kort grøn streg */}
              <div className="w-12 h-[2px] bg-petrol-dark/80 mb-6" />

              <div className="space-y-4 font-sans text-base sm:text-lg text-text-dark/85 leading-relaxed [&>p]:mb-4 [&>p:last-child]:mb-0">
                {cmsData?.former_intro_tekst ? (
                  <div dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsData.former_intro_tekst) }} />
                ) : (
                  <>
                    <p>
                      OCD handler sjældent om kun én ting. Tvangstanker kan kredse om mange forskellige temaer – fx forurening, kontrol, aggressive tanker, seksuelle eller religiøse temaer, tvivl om relationer eller en vedvarende følelse af, at noget ikke er helt rigtigt.
                    </p>
                    <p>
                      Selvom indholdet varierer, er mønstret det samme: uønskede tanker skaber angst, som forsøges dæmpet med ritualer eller kontrol – midlertidigt.
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Højre kolonne */}
            <div className="flex justify-center lg:justify-end">
              <div className="w-full max-w-xl lg:max-w-2xl">
                <img
                  src={cmsData?.former_billede?.[0] ? cmsData.former_billede[0] : "/_assets/behandling-af-ocd-illustration.jpg"}
                  alt="OCD illustration"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ==========================================
          SEKTION 7 – FORSKELLIGE TYPER AF OCD
          ========================================== */}
      <section className="py-16 lg:py-24 bg-sand-light border-b border-sand-medium/40">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-display text-3xl sm:text-4xl font-normal text-petrol-dark tracking-tight mb-3">
              {cmsData?.typer_overskrift ? getLocalizedValue(cmsData.typer_overskrift) : (lang === 'da' ? 'Forskellige typer af OCD' : 'Different Subtypes of OCD')}
            </h2>
            {/* Kort grøn streg */}
            <div className="w-12 h-[2px] bg-petrol-dark/80 mx-auto" />
          </div>

          {/* Seks ens kort i 3 kolonner og 2 rækker */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            
            {/* Kort 1 */}
            <div className="rounded-3xl p-8 bg-[#D1E1BE] border border-[#D1E1BE] shadow-xs flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-white/80 border border-petrol-dark/10 flex items-center justify-center text-petrol-dark mb-5">
                <i className="fa-regular fa-bacteria text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Kontaminations-OCD' : 'Contamination OCD'}
              </h3>
              <p className="font-sans text-sm text-petrol-dark/85 leading-relaxed">
                {lang === 'da' ? 'Frygt for bakterier, sygdom eller smitte.' : 'Fear of germs, illness, dirt, or chemical contamination.'}
              </p>
            </div>

            {/* Kort 2 */}
            <div className="rounded-3xl p-8 bg-[#D1E1BE] border border-[#D1E1BE] shadow-xs flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-white/80 border border-petrol-dark/10 flex items-center justify-center text-petrol-dark mb-5">
                <i className="fa-regular fa-lock text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Kontrol-OCD' : 'Checking OCD'}
              </h3>
              <p className="font-sans text-sm text-petrol-dark/85 leading-relaxed">
                {lang === 'da' ? 'Gentagen kontrol af døre, komfur eller apparater.' : 'Repetitive checking of doors, stoves, switches, or locks.'}
              </p>
            </div>

            {/* Kort 3 */}
            <div className="rounded-3xl p-8 bg-[#D1E1BE] border border-[#D1E1BE] shadow-xs flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-white/80 border border-petrol-dark/10 flex items-center justify-center text-petrol-dark mb-5">
                <i className="fa-regular fa-cloud-bolt text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Aggressive tvangstanker' : 'Harm & Aggressive OCD'}
              </h3>
              <p className="font-sans text-sm text-petrol-dark/85 leading-relaxed">
                {lang === 'da' ? 'Uønskede tanker om at skade sig selv eller andre.' : 'Unwanted, distressing intrusive thoughts of harming oneself or others.'}
              </p>
            </div>

            {/* Kort 4 */}
            <div className="rounded-3xl p-8 bg-[#D1E1BE] border border-[#D1E1BE] shadow-xs flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-white/80 border border-petrol-dark/10 flex items-center justify-center text-petrol-dark mb-5">
                <i className="fa-regular fa-user-group text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Relations-OCD' : 'Relationship OCD (ROCD)'}
              </h3>
              <p className="font-sans text-sm text-petrol-dark/85 leading-relaxed">
                {lang === 'da' ? 'Tvivl om følelser, relationer eller om noget føles rigtigt.' : 'Obsessive doubt regarding romantic feelings, partner compatibility, or trust.'}
              </p>
            </div>

            {/* Kort 5 */}
            <div className="rounded-3xl p-8 bg-[#D1E1BE] border border-[#D1E1BE] shadow-xs flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-white/80 border border-petrol-dark/10 flex items-center justify-center text-petrol-dark mb-5">
                <i className="fa-regular fa-border-all text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Symmetri og orden' : 'Symmetry & Just-Right OCD'}
              </h3>
              <p className="font-sans text-sm text-petrol-dark/85 leading-relaxed">
                {lang === 'da' ? 'Behov for at ting føles helt rigtige.' : 'The overwhelming urge for items to align evenly or feel ‘just right’.'}
              </p>
            </div>

            {/* Kort 6 */}
            <div className="rounded-3xl p-8 bg-[#D1E1BE] border border-[#D1E1BE] shadow-xs flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-white/80 border border-petrol-dark/10 flex items-center justify-center text-petrol-dark mb-5">
                <i className="fa-regular fa-church text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Religiøs eller moralsk OCD' : 'Scrupulosity / Moral OCD'}
              </h3>
              <p className="font-sans text-sm text-petrol-dark/85 leading-relaxed">
                {lang === 'da' ? 'Tvangstanker om skyld, synd eller moral.' : 'Obsessive preoccupation with guilt, moral purity, ethics, or blasphemy.'}
              </p>
            </div>

          </div>
        </div>
      </section>


      {/* ==========================================
          SEKTION 8 – OCD-CIRKLEN
          ========================================== */}
      <section className="pt-6 sm:pt-8 lg:pt-10 pb-16 lg:pb-24 bg-sand-light">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-normal text-petrol-dark tracking-tight mb-3">
              {cmsData?.cirkel_overskrift ? getLocalizedValue(cmsData.cirkel_overskrift) : 'OCD-cirklen'}
            </h2>
            {/* Kort grøn streg */}
            <div className="w-12 h-[2px] bg-petrol-dark/80 mx-auto mb-6" />
            
            <p className="font-display italic text-lg sm:text-xl text-petrol-dark font-medium leading-relaxed">
              {cmsData?.cirkel_undertekst ? getLocalizedValue(cmsData.cirkel_undertekst) : (
                <>
                  Tvangshandlinger giver kortvarig lettelse, men fastholder angsten i en ond cirkel.
                  <br className="hidden sm:inline" />{' '}
                  Behandling retter sig mod at bryde den.
                </>
              )}
            </p>
          </div>

          {/* Cirklen proces diagram */}
          <div className="relative max-w-5xl mx-auto pb-12">
            
            {/* Desktop & Tablet: Vandret forløb med 5 trin */}
            <div className="hidden sm:flex flex-row items-start justify-between relative z-10 max-w-4xl mx-auto">
              
              {/* Trin 1 */}
              <div className="flex-1 flex flex-col items-center text-center px-1 max-w-[140px]">
                <div className="w-16 h-16 rounded-full bg-[#eee9df] border border-petrol-dark/20 flex items-center justify-center text-petrol-dark mb-3 shadow-xs">
                  <i className="fa-regular fa-brain text-2xl" aria-hidden="true" />
                </div>
                <h4 className="font-display font-medium text-base text-petrol-dark">
                  {lang === 'da' ? 'Tvangstanke' : 'Obsession'}
                </h4>
              </div>

              {/* Pil 1 -> 2 */}
              <div className="flex items-center justify-center h-16 text-petrol-dark/60 text-lg px-1 shrink-0">
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </div>

              {/* Trin 2 */}
              <div className="flex-1 flex flex-col items-center text-center px-1 max-w-[140px]">
                <div className="w-16 h-16 rounded-full bg-[#eee9df] border border-petrol-dark/20 flex items-center justify-center text-petrol-dark mb-3 shadow-xs">
                  <i className="fa-regular fa-head-side-brain text-2xl" aria-hidden="true" />
                </div>
                <h4 className="font-display font-medium text-base text-petrol-dark">
                  {lang === 'da' ? 'Angst' : 'Anxiety / Distress'}
                </h4>
              </div>

              {/* Pil 2 -> 3 */}
              <div className="flex items-center justify-center h-16 text-petrol-dark/60 text-lg px-1 shrink-0">
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </div>

              {/* Trin 3 */}
              <div className="flex-1 flex flex-col items-center text-center px-1 max-w-[140px]">
                <div className="w-16 h-16 rounded-full bg-[#eee9df] border border-petrol-dark/20 flex items-center justify-center text-petrol-dark mb-3 shadow-xs">
                  <i className="fa-regular fa-rotate text-2xl" aria-hidden="true" />
                </div>
                <h4 className="font-display font-medium text-base text-petrol-dark">
                  {lang === 'da' ? 'Tvangshandling' : 'Compulsion'}
                </h4>
              </div>

              {/* Pil 3 -> 4 */}
              <div className="flex items-center justify-center h-16 text-petrol-dark/60 text-lg px-1 shrink-0">
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </div>

              {/* Trin 4 */}
              <div className="flex-1 flex flex-col items-center text-center px-1 max-w-[140px]">
                <div className="w-16 h-16 rounded-full bg-[#eee9df] border border-petrol-dark/20 flex items-center justify-center text-petrol-dark mb-3 shadow-xs">
                  <i className="fa-regular fa-face-smile text-2xl" aria-hidden="true" />
                </div>
                <h4 className="font-display font-medium text-base text-petrol-dark">
                  {lang === 'da' ? 'Kortvarig lettelse' : 'Temporary Relief'}
                </h4>
              </div>

              {/* Pil 4 -> 5 */}
              <div className="flex items-center justify-center h-16 text-petrol-dark/60 text-lg px-1 shrink-0">
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </div>

              {/* Trin 5 */}
              <div className="flex-1 flex flex-col items-center text-center px-1 max-w-[140px]">
                <div className="w-16 h-16 rounded-full bg-[#eee9df] border border-petrol-dark/20 flex items-center justify-center text-petrol-dark mb-3 shadow-xs">
                  <i className="fa-regular fa-circle-question text-2xl" aria-hidden="true" />
                </div>
                <h4 className="font-display font-medium text-base text-petrol-dark">
                  {lang === 'da' ? 'Mere tvivl og usikkerhed' : 'Doubt & Reinforced Anxiety'}
                </h4>
              </div>

            </div>

            {/* Desktop visual return loop arrow curve connecting step 5 back to step 1 */}
            <div className="hidden sm:block relative mt-6 h-14">
              <svg 
                className="w-full h-full text-petrol-dark/50 overflow-visible pointer-events-none" 
                viewBox="0 0 1000 48" 
                preserveAspectRatio="none"
              >
                <path 
                  d="M 900,0 L 900,24 Q 900,44 870,44 L 130,44 Q 100,44 100,24 L 100,8" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  vectorEffect="non-scaling-stroke"
                />
                <polygon 
                  points="100,0 94,10 106,10" 
                  fill="currentColor" 
                />
              </svg>
            </div>

            {/* Mobil: Lodret forløb med pile og tilbagegående forklaring */}
            <div className="sm:hidden flex flex-col items-center space-y-4">
              {/* Trin 1 */}
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-[#eee9df] border border-petrol-dark/20 flex items-center justify-center text-petrol-dark mb-2 shadow-xs">
                  <i className="fa-regular fa-brain text-2xl" aria-hidden="true" />
                </div>
                <h4 className="font-display font-medium text-base text-petrol-dark">
                  {lang === 'da' ? 'Tvangstanke' : 'Obsession'}
                </h4>
              </div>

              <i className="fa-solid fa-arrow-down text-petrol-dark/60 my-1" aria-hidden="true" />

              {/* Trin 2 */}
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-[#eee9df] border border-petrol-dark/20 flex items-center justify-center text-petrol-dark mb-2 shadow-xs">
                  <i className="fa-regular fa-head-side-brain text-2xl" aria-hidden="true" />
                </div>
                <h4 className="font-display font-medium text-base text-petrol-dark">
                  {lang === 'da' ? 'Angst' : 'Anxiety / Distress'}
                </h4>
              </div>

              <i className="fa-solid fa-arrow-down text-petrol-dark/60 my-1" aria-hidden="true" />

              {/* Trin 3 */}
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-[#eee9df] border border-petrol-dark/20 flex items-center justify-center text-petrol-dark mb-2 shadow-xs">
                  <i className="fa-regular fa-rotate text-2xl" aria-hidden="true" />
                </div>
                <h4 className="font-display font-medium text-base text-petrol-dark">
                  {lang === 'da' ? 'Tvangshandling' : 'Compulsion'}
                </h4>
              </div>

              <i className="fa-solid fa-arrow-down text-petrol-dark/60 my-1" aria-hidden="true" />

              {/* Trin 4 */}
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-[#eee9df] border border-petrol-dark/20 flex items-center justify-center text-petrol-dark mb-2 shadow-xs">
                  <i className="fa-regular fa-face-smile text-2xl" aria-hidden="true" />
                </div>
                <h4 className="font-display font-medium text-base text-petrol-dark">
                  {lang === 'da' ? 'Kortvarig lettelse' : 'Temporary Relief'}
                </h4>
              </div>

              <i className="fa-solid fa-arrow-down text-petrol-dark/60 my-1" aria-hidden="true" />

              {/* Trin 5 */}
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-[#eee9df] border border-petrol-dark/20 flex items-center justify-center text-petrol-dark mb-2 shadow-xs">
                  <i className="fa-regular fa-circle-question text-2xl" aria-hidden="true" />
                </div>
                <h4 className="font-display font-medium text-base text-petrol-dark">
                  {lang === 'da' ? 'Mere tvivl og usikkerhed' : 'Doubt & Reinforced Anxiety'}
                </h4>
              </div>

              <div className="pt-4 flex items-center gap-2 text-petrol-dark/70 text-sm italic font-display">
                <i className="fa-solid fa-rotate-left" aria-hidden="true" />
                <span>{lang === 'da' ? 'Processen gentager sig i en cyklus' : 'The cycle repeats continuously'}</span>
              </div>
            </div>

          </div>

          {/* Centrerede tekst i serif-font under diagrammet - rykket op under overskriften */}

        </div>
      </section>

      {/* CTA Section */}
      <CtaSection 
        title={cmsData?.cta_overskrift ? getLocalizedValue(cmsData.cta_overskrift) : (lang === 'da' ? 'OCD kan behandles.' : 'OCD can be treated.')}
        desc={cmsData?.cta_beskrivelse ? getLocalizedValue(cmsData.cta_beskrivelse) : (lang === 'da' 
          ? 'Med den rette behandling er det muligt at få mere frihed fra tvangstanker og tvangshandlinger. Behandlingen tilrettelægges individuelt og tager udgangspunkt i, hvordan OCD påvirker netop din hverdag.'
          : 'With the right treatment, it is possible to gain greater freedom from obsessions and compulsions. Treatment is tailored individually and based on how OCD affects your daily life.'
        )}
        buttonText={cmsData?.cta_knap_tekst ? getLocalizedValue(cmsData.cta_knap_tekst) : (lang === 'da' ? 'Book forsamtale' : 'Book initial consultation')}
        buttonLink={cmsData?.cta_knap_link || cmsData?.hero_cta_link || "/kontakt"}
      />

    </div>
  );
}
